import Service from '@/App/Service/Entity/Service'
import ServiceID from '@/App/Service/Entity/ServiceID'
import { ensureNoUndefinedProperties } from '@/Shared/Helper/EnsureNoUndefinedProperties'
import User from '@/App/User/Entity/User'
import CompanyUser from '@/App/CompanyUser/Entity/CompanyUser'
import Availability from '@/App/Availability/Entity/Availability'
import AvailabilityID from '@/App/Availability/Entity/AvailabilityID'
import type EntityWithAssociatedAvailabilityInterface from '@/App/Availability/Entity/EntityWithAssociatedAvailabilityInterface'
import { RequestBodyKeyEnum } from '@/Shared/AutoGenerated/RequestFieldEnum'
import type { CompanyUserAggregateData } from '@/Shared/AutoGenerated/Main'

export default class CompanyUserAggregate implements EntityWithAssociatedAvailabilityInterface {
    constructor(
        public readonly companyUser: CompanyUser,
        public readonly user: User,
        public readonly availability: Availability | null,
        public readonly services: Service[],
    ) {
        ensureNoUndefinedProperties(this)
    }

    isServiceSelected(serviceId: ServiceID): boolean {
        return !!this.services.find((service) => {
            return service.id.value === serviceId.value
        })
    }

    getAvailabilityName(): string {
        return this.availability ? this.availability.name : 'None'
    }

    // ------------------------
    // EntityWithAssociatedAvailabilityInterface implementation
    // ------------------------

    getCurrentlyAssociatedAvailabilityId(): AvailabilityID | null {
        return this.availability?.id ?? null
    }

    getSectionTitle(): string {
        return 'Availability'
    }

    getRequestBodyFieldName(): RequestBodyKeyEnum {
        return RequestBodyKeyEnum.App_CompanyUser_Availability_Id
    }

    // ------------------------
    // Hydration
    // ------------------------

    static hydrateSingle(
        entityData: CompanyUserAggregateData,
    ): CompanyUserAggregate {
        return new CompanyUserAggregate(
            CompanyUser.hydrateSingle(entityData.companyUser),
            User.hydrateSingle(entityData.user),
            Availability.hydrateSingleOrNull(entityData.availability),
            Service.hydrateMultiple(entityData.services),
        )
    }

    static hydrateMultiple(
        entitiesData: CompanyUserAggregateData[],
    ): CompanyUserAggregate[] {
        return entitiesData.map((entityData) => this.hydrateSingle(entityData))
    }
}
